/* ==========================================================================
   Variant video swap (Zippy Dynamics)
   --------------------------------------------------------------------------
   When a product has the boolean metafield custom.variant_video_swap = true,
   snippets/variant-video-swap.liquid emits a JSON blob with a per-color VIDEO
   URL map (videos keyed by color) + a per-color POSTER map (the color's image).

   The videos are NOT in the product's media gallery (keeping the gallery — and
   page load — fast). They're referenced by direct URL, matched to the selected
   color by name.

   The swap target is the FIRST native <video> the merchant embedded in the
   product description (the demo clip). Gallery media videos (inside
   deferred-media/.product-media) and the <video> this script creates are
   ignored. (Originally the description held a YouTube iframe; that embed is now
   a native <video>, so we target the <video> instead.)

   Behavior:
     - On the selected color (and every color change) look up that color's video
       URL by color name.
     - If a video exists -> swap the description's embedded <video> out for a
       native <video> of that color, using the color's PRODUCT IMAGE as the
       poster/thumbnail. It does not autoplay (click to play).
     - If NO video exists -> show the original embedded <video> (restoring it if
       it had been swapped away).
     - keepYoutubeColors forces specific colors to keep the original embed (the
       baseline clip is the green variant). Set via custom.variant_video_youtube_color
       (comma-separated); defaults to "apple green".
   ========================================================================== */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function tokenize(str) {
    if (!str) return [];
    return String(str)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(' ')
      .filter(Boolean);
  }

  function normKey(str) {
    return tokenize(str).join('');
  }

  // The swap target is a native <video> the merchant placed in the description.
  // Exclude the videos THIS script creates (.variant-video-swap__video) and the
  // gallery's media videos (which live inside deferred-media/.product-media).
  var EMBED_SELECTOR = 'video:not(.variant-video-swap__video)';

  function findDescriptionEmbed(root) {
    var node = root && root.querySelectorAll ? root : document;
    var list = node.querySelectorAll(EMBED_SELECTOR);
    for (var i = 0; i < list.length; i++) {
      if (!list[i].closest('deferred-media, .product-media')) return list[i];
    }
    return null;
  }

  ready(function () {
    document
      .querySelectorAll('script[type="application/json"][data-variant-video-swap]')
      .forEach(initSwap);
  });

  function initSwap(dataEl) {
    var config;
    try {
      config = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!config || !config.enabled || !config.videos) return;

    var productId = String(config.productId);

    // Per-color video URL, keyed by normalized color name (handle or value).
    var videoLookup = {};
    Object.keys(config.videos).forEach(function (k) {
      if (config.videos[k]) videoLookup[normKey(k)] = config.videos[k];
    });
    if (!Object.keys(videoLookup).length) return;

    // Per-color product image, keyed by normalized color name.
    var posterLookup = {};
    var rawPosters = config.posters || {};
    Object.keys(rawPosters).forEach(function (k) {
      if (rawPosters[k]) posterLookup[normKey(k)] = rawPosters[k];
    });

    // Colors that should always keep the original embed (JSON key kept as
    // keepYoutubeColors for back-compat with the metafield-driven liquid config).
    var keep = config.keepYoutubeColors;
    if (typeof keep === 'string') keep = keep.split(',');
    var keepTokenSets = (Array.isArray(keep) ? keep : [])
      .map(tokenize)
      .filter(function (t) { return t.length; });

    var scope =
      dataEl.closest('product-info') ||
      document.getElementById('MainProduct-' + productId) ||
      document;

    var embedNode = null;   // original description <video> (may be detached)
    var anchor = null;      // comment node marking the slot
    var videoNode = null;   // <video> (created lazily, may be detached)
    var lastColorKey = null;
    var embedWidth = 560;   // measured from the embed so swapped videos match it
    var embedAspect = 1.7778;

    function mainVariantSelects() {
      var list = document.querySelectorAll(
        'variant-selects[data-product-id="' + productId + '"]'
      );
      for (var i = 0; i < list.length; i++) {
        var ctx = list[i].getAttribute('data-context') || '';
        if (ctx.indexOf('sticky') !== 0) return list[i];
      }
      return list[0] || null;
    }

    function selectedColor() {
      var vs = mainVariantSelects();
      if (!vs) return null;
      var checked = vs.querySelectorAll('input[type="radio"]:checked');
      for (var i = 0; i < checked.length; i++) {
        var label = checked[i].nextElementSibling;
        var swatch = label && label.querySelector ? label.querySelector('.swatch') : null;
        if (swatch) {
          return swatch.getAttribute('data-title') || checked[i].value || null;
        }
      }
      var sv = vs.querySelector('.product-form__input--swatch [data-selected-value]');
      return sv ? sv.textContent.trim() : null;
    }

    function findVideoForColor(color) {
      var src = videoLookup[normKey(color)];
      return src ? { src: src } : null;
    }

    function shouldKeepOriginal(color) {
      var ctoks = tokenize(color);
      return keepTokenSets.some(function (kt) {
        return kt.every(function (t) { return ctoks.indexOf(t) !== -1; });
      });
    }

    function ensureSetup() {
      if (anchor) return true;
      var embed = findDescriptionEmbed(scope) || findDescriptionEmbed(document);
      if (!embed || !embed.parentNode) return false;

      // Measure the embed so swapped videos render at the same size.
      var rect = embed.getBoundingClientRect();
      var attrW = parseInt(embed.getAttribute('width'), 10);
      var attrH = parseInt(embed.getAttribute('height'), 10);
      var w = Math.round(rect.width) || embed.offsetWidth || attrW || 560;
      var h = Math.round(rect.height) || embed.offsetHeight || attrH || 0;
      embedWidth = w;
      if (w && h) embedAspect = w / h;
      else if (attrW && attrH) embedAspect = attrW / attrH;

      embedNode = embed;
      anchor = document.createComment('variant-video-swap');
      embed.parentNode.insertBefore(anchor, embed);
      return true;
    }

    function makeVideo(src, poster) {
      var el = document.createElement('video');
      el.className = 'variant-video-swap__video';
      el.setAttribute('playsinline', '');
      el.setAttribute('controls', '');
      el.setAttribute('preload', 'metadata');
      if (poster) el.setAttribute('poster', poster);
      // Match the embed's footprint exactly.
      el.style.cssText =
        'display:block;width:100%;max-width:' + embedWidth + 'px;aspect-ratio:' +
        embedAspect + ';object-fit:cover;background:transparent;';
      el.src = src;
      return el;
    }

    function showOriginalEmbed() {
      if (!ensureSetup()) return false;
      if (videoNode && videoNode.parentNode) {
        try { videoNode.pause(); } catch (e) {}
        videoNode.parentNode.removeChild(videoNode);
      }
      if (embedNode && !embedNode.parentNode && anchor.parentNode) {
        anchor.parentNode.insertBefore(embedNode, anchor.nextSibling);
      }
      return true;
    }

    function showVideo(v, poster) {
      if (!ensureSetup()) return false;
      if (!videoNode) {
        videoNode = makeVideo(v.src, poster);
      } else if (videoNode.getAttribute('src') !== v.src) {
        if (poster) videoNode.setAttribute('poster', poster);
        else videoNode.removeAttribute('poster');
        videoNode.src = v.src;
        videoNode.load();
      } else if (poster && videoNode.getAttribute('poster') !== poster) {
        videoNode.setAttribute('poster', poster);
      }
      if (embedNode && embedNode.parentNode) {
        embedNode.parentNode.removeChild(embedNode);
      }
      if (!videoNode.parentNode && anchor.parentNode) {
        anchor.parentNode.insertBefore(videoNode, anchor.nextSibling);
      }
      return true;
    }

    // Returns true only when the swap actually happened (the embed was found and
    // the right media is in place). Only then do we record the color, so a swap
    // that bailed because the description wasn't ready yet gets retried.
    function applyColor(color) {
      if (!color) return false;
      var key = normKey(color);
      if (key === lastColorKey && (videoNode || (embedNode && embedNode.parentNode))) {
        return true;
      }
      var done;
      if (shouldKeepOriginal(color)) {
        done = showOriginalEmbed();
      } else {
        var v = findVideoForColor(color);
        done = v ? showVideo(v, posterLookup[key] || null) : showOriginalEmbed();
      }
      if (done) lastColorKey = key;
      return done;
    }

    function onChange(e) {
      var vs = e.target.closest ? e.target.closest('variant-selects') : null;
      if (!vs) return;
      if ((vs.getAttribute('data-product-id') || '') !== productId) return;
      if ((vs.getAttribute('data-context') || '').indexOf('sticky') === 0) return;
      applyColor(selectedColor());
    }

    document.addEventListener('change', onChange);

    // Initial swap for the on-load variant. Retry until the swap actually
    // applies (both the variant picker AND the description <video> are ready),
    // so a reload reliably switches to the selected variant.
    var tries = 0;
    (function initialApply() {
      if (applyColor(selectedColor())) return;
      if (tries++ < 50) setTimeout(initialApply, 100);
    })();
  }
})();
